import React from "react";
import { ListItem, Box, TextField, Typography, IconButton } from "@mui/material";
import {
  DragIndicator as DragIcon,
  ContentCopy as CopyIcon,
} from "@mui/icons-material";
import { type TabData } from "@utils/dashboard-storage";
import HPButton from "@/components/common/Button";
import { RemoveIcon, RemoveIconRed, WarningIconBlack } from "@/assets/images";

interface TabRowConfirmation {
  hasContent: boolean;
  affectedGroupName: string | null;
}

interface TabRowProps {
  tab: TabData;
  isDragging: boolean;
  isDraggedOver: boolean;
  confirmation: TabRowConfirmation | null;
  error?: string;
  isHovered: boolean;
  isDuplicatingTab: boolean;
  canDelete: boolean;
  canDuplicate: boolean;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragEnter: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onNameChange: (value: string) => void;
  onNameBlur: (value: string) => void;
  onDeleteClick: () => void;
  onDuplicateClick: () => void;
  onConfirmDelete: () => void;
  onCancelDelete: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

const TabRow: React.FC<TabRowProps> = ({
  tab,
  isDragging,
  isDraggedOver,
  confirmation,
  error,
  isHovered,
  isDuplicatingTab,
  canDelete,
  canDuplicate,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragEnter,
  onDragLeave,
  onDrop,
  onNameChange,
  onNameBlur,
  onDeleteClick,
  onDuplicateClick,
  onConfirmDelete,
  onCancelDelete,
  onMouseEnter,
  onMouseLeave,
}) => {
  return (
    <ListItem
      disableGutters
      disablePadding
      draggable={!confirmation}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      sx={{
        display: "block",
        p: 0,
        boxSizing: "border-box",
        cursor: confirmation ? "default" : "grab",
        opacity: isDragging ? 0.5 : 1,
        mb: "10px",
      }}
    >
      {/* Insertion line: dropping here places the dragged item before this
          tab. Inset to match the tab's own width, not the full row. */}
      <Box
        sx={{
          height: 0,
          mx: 2,
          borderTop: isDraggedOver ? "3px dashed #1976d2" : "3px dashed transparent",
        }}
      />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          padding: "0 16px",
        }}
      >
        <Box sx={{ flexGrow: 1, my: 1 }}>
          {confirmation ? (
            <Box sx={{ display: "flex", alignItems: "center", padding: "8px 12px", gap: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <WarningIconBlack />
              </Box>
              <Typography variant="body2" sx={{ flexGrow: 1, fontSize: "14px" }}>
                {confirmation.hasContent
                  ? confirmation.affectedGroupName
                    ? `There are widgets in this tab, and it's the last tab in "${confirmation.affectedGroupName}". Deleting it will remove the tab and dissolve the group. Are you sure?`
                    : "There are widgets in this tab. Are you sure you want to delete it?"
                  : confirmation.affectedGroupName
                    ? `This is the last remaining tab in "${confirmation.affectedGroupName}". Deleting it will dissolve the group. Are you sure you want to delete this tab?`
                    : "Are you sure you want to delete this tab?"}
              </Typography>
              <Box sx={{ display: "flex", gap: 0 }}>
                <HPButton
                  variant="text"
                  onClick={onConfirmDelete}
                  className="px-[0px] font-bold"
                  color="error"
                >
                  Delete
                </HPButton>
                <HPButton variant="text" onClick={onCancelDelete} className="px-[0px] font-bold">
                  Cancel
                </HPButton>
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
              <DragIcon sx={{ mr: 2, color: "text.secondary" }} />

              <Box sx={{ flexGrow: 1 }}>
                <TextField
                  size="small"
                  value={tab.label}
                  onChange={(e) => onNameChange(e.target.value)}
                  onBlur={(e) => onNameBlur(e.target.value)}
                  inputProps={{ maxLength: 40 }}
                  fullWidth
                  error={!!error}
                  helperText={error}
                  InputProps={{
                    sx: { borderRadius: "8px" },
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>
        {!confirmation && (
          <>
            <IconButton
              aria-label="delete"
              onClick={onDeleteClick}
              disabled={!canDelete}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              sx={{ ml: 1, color: "text.secondary", "&:hover": { color: "#D80000" } }}
            >
              {isHovered ? <RemoveIconRed /> : <RemoveIcon />}
            </IconButton>
            <IconButton
              aria-label="duplicate"
              onClick={onDuplicateClick}
              disabled={isDuplicatingTab || !canDuplicate}
              title={canDuplicate ? "Duplicate tab" : "Max 20 tabs reached"}
              sx={{
                ml: 1,
                color: "black",
                "&:hover": { color: isDuplicatingTab || !canDuplicate ? "inherit" : "primary.main" },
              }}
            >
              <CopyIcon className="w-[24px]" />
            </IconButton>
          </>
        )}
      </Box>
    </ListItem>
  );
};

export default TabRow;
